<template>
  <div class="page">
    <header class="page-header">
      <div class="page-header-bar">绑定终端</div>
      <div class="page-header-content">
        <h1>第二步：定位安装</h1>
        <p>已识别区域码，请完成安装定位信息</p>
      </div>
    </header>

    <main class="page-body">
      <div class="page-body-scroll">
        <!-- 1. 区域信息卡片 -->
        <section class="panel area-panel">
          <div class="panel-head">
            <div class="panel-title">区域信息</div>
            <van-button plain size="small" type="primary" @click="goScanArea">切换区域</van-button>
          </div>

          <div class="area-row">
            <div class="panel-label">所属区域：</div>
            <div class="panel-value">{{ areaName }}</div>
          </div>
        </section>

        <!-- 2. 安装定位表单 -->
        <section class="panel form-panel">
          <div class="panel-title">安装定位信息</div>
          <div class="select-list">
            <InstallSelect
              v-model="form.buildingId"
              label="所属楼宇"
              title="选择所属楼宇"
              placeholder="请选择所属楼宇"
              :options="buildingOptions"
              filterable
              search-placeholder="请输入楼宇名称"
              @change="onBuildingChange"
            />

            <InstallSelect
              v-model="form.shaftId"
              label="所属电梯井"
              title="选择所属电梯井"
              placeholder="请选择所属电梯井"
              :options="shaftOptions"
              :disabled="!form.buildingId"
              filterable
              search-placeholder="请输入电梯井名称"
            />

            <div v-if="shaftLocationText" class="location-hint">对应位置：{{ shaftLocationText }}</div>

            <InstallSelect
              v-model="form.deviceType"
              label="设备类型"
              title="选择设备类型"
              placeholder="请选择设备类型"
              :options="deviceTypeOptions"
            />

            <InstallSelect
              v-model="form.installPosition"
              label="安装位置"
              title="选择安装位置"
              placeholder="请选择安装位置"
              :options="installPositionOptions"
            />
          </div>
        </section>
      </div>
    </main>

    <footer class="page-footer safe-bottom">
      <van-button block type="primary" :disabled="!isFormComplete || resolvingTerminal" :loading="resolvingTerminal" @click="goNext">
        确认并进行绑定
      </van-button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import { resolveTerminal } from '@/api/install'
import InstallSelect from '@/components/install/InstallSelect.vue'
import { useMainStore } from '@/store'

const router = useRouter()
const store = useMainStore()
const installContext = computed(() => store.installContext)
const resolvingTerminal = ref(false)

if (!installContext.value) {
  router.replace('/install/area')
}

const areaName = computed(() => installContext.value?.regionName || '')

const form = reactive({
  buildingId: store.installSelection.buildingId ? String(store.installSelection.buildingId) : '',
  shaftId: store.installSelection.shaftId ? String(store.installSelection.shaftId) : '',
  deviceType: store.installSelection.deviceType || '智能屏',
  installPosition: store.installSelection.installPosition || '轿厢内'
})

type ShaftOption = {
  shaftId: number
  name: string
  location?: string
}

type BuildingOption = {
  regionId: number
  regionName: string
  buildingId: number
  name: string
  shafts: ShaftOption[]
}

const buildingList = computed<BuildingOption[]>(() => {
  return (installContext.value?.buildings || []).map(item => ({
    regionId: item.regionId,
    regionName: item.regionName,
    buildingId: item.buildingId,
    name: item.buildingName,
    shafts: item.shafts.map(shaft => ({
      shaftId: shaft.shaftId,
      name: shaft.shaftName,
      location: shaft.position
    }))
  }))
})

const buildingOptions = computed(() =>
  buildingList.value.map(item => ({ value: String(item.buildingId), label: item.name }))
)
const deviceTypeOptions = [{ value: '智能屏', label: '智能屏' }]
const installPositionOptions = [{ value: '轿厢内', label: '轿厢内' }]

const currentBuilding = computed(() => {
  return buildingList.value.find(item => String(item.buildingId) === form.buildingId) || null
})

const currentShaft = computed(() => {
  if (!currentBuilding.value) {
    return null
  }

  return currentBuilding.value.shafts.find(item => String(item.shaftId) === form.shaftId) || null
})

const shaftOptions = computed(() => {
  return currentBuilding.value?.shafts.map(item => ({ value: String(item.shaftId), label: item.name })) || []
})

const shaftLocationText = computed(() => {
  return currentShaft.value?.location || ''
})

const isFormComplete = computed(() => {
  return Boolean(form.buildingId && form.shaftId && form.deviceType && form.installPosition)
})

watch(() => form.deviceType, value => {
  store.setInstallSelection({ deviceType: value })
})

watch(() => form.installPosition, value => {
  store.setInstallSelection({ installPosition: value })
})

const onBuildingChange = (value: string) => {
  form.buildingId = value
  form.shaftId = ''
}

const goScanArea = () => {
  router.push('/install/area')
}

const goNext = () => {
  void submitAndNext()
}

const submitAndNext = async () => {
  if (!isFormComplete.value) {
    showToast('请完整选择楼宇、电梯井、设备类型和安装位置')
    return
  }

  const building = currentBuilding.value
  const shaft = currentShaft.value
  if (!building || !shaft) {
    showToast('请选择有效的楼宇和电梯井')
    return
  }

  if (!installContext.value) {
    showToast('缺少区域信息，请重新扫码区域码')
    router.replace('/install/area')
    return
  }

  resolvingTerminal.value = true
  try {
    const resp = await resolveTerminal({
      regionId: building.regionId,
      buildingId: building.buildingId,
      shaftId: shaft.shaftId
    })
    store.setInstallSelection({
      terminalId: resp.data.terminalId,
      terminalCode: '',
      buildingId: building.buildingId,
      buildingName: building.name,
      shaftId: shaft.shaftId,
      shaftName: shaft.name,
      deviceType: form.deviceType,
      installPosition: form.installPosition
    })
    router.push('/install/device')
  } catch (error) {
    showToast(error instanceof Error ? error.message : '终端解析失败，请稍后重试')
  } finally {
    resolvingTerminal.value = false
  }
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
  background: #f7f8fa;
  padding-top: calc(env(safe-area-inset-top) + 48px);
}

.page-header {
  flex: none;
  text-align: center;
}

.page-header-bar {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(env(safe-area-inset-top) + 48px);
  padding-top: env(safe-area-inset-top);
  box-sizing: border-box;
  background: #3e87f8;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
}

.page-header-content {
  padding: 16px 0 8px;
}

.page-header h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1.4;
  color: #1f2937;
}

.page-header p {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b7280;
}

.page-body {
  flex: 1;
  min-height: 0;
  margin: 8px 10px 0;
  padding: 16px;
  overflow: hidden;
  box-sizing: border-box;
  border-radius: 16px;
  background: #eef0f4;
}

.page-body-scroll {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.page-body-scroll::-webkit-scrollbar {
  display: none;
}

.panel {
  padding: 16px;
  border-radius: 8px;
  background: #fff;
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.area-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.panel-label {
  flex: none;
  font-size: 14px;
  color: #6b7280;
}

.panel-value {
  min-width: 0;
  flex: 1;
  font-size: 16px;
  line-height: 1.5;
  word-break: break-all;
  color: #111827;
}

.panel :deep(.van-button) {
  flex: none;
  min-width: 88px;
  border-color: #3e87f8;
  border-radius: 999px;
}

.form-panel {
  padding: 16px;
}

.form-panel .panel-title {
  margin-bottom: 16px;
}

.select-list {
  display: grid;
  gap: 12px;
}

.select-list :deep(.select-trigger) {
  margin-bottom: 12px;
}

.select-list :deep(.install-select:last-child .select-trigger) {
  margin-bottom: 0;
}

.location-hint {
  margin: -4px 0 4px 16px;
  font-size: 14px;
  color: #6b7280;
}

.page-footer {
  flex: none;
  padding: 8px 16px 0;
}

.safe-bottom {
  padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
}
</style>
